/**
 * CalendarEventDetailsDialog Component
 * Read-only dialog for viewing external calendar event details (Google/Microsoft).
 */

import {
  Calendar,
  Clock,
  MapPin,
  FileText,
  Globe,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import type { CalendarEventDto } from '@signalvote/shared';
import { getTimePartsInTimezone, getTimezoneAbbreviation } from '@/lib/calendarUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// ============================================================================
// Types
// ============================================================================

interface CalendarEventDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  event: CalendarEventDto | null;
  timeZoneId?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function formatEventTime(isoString: string, timeZoneId?: string): string {
  const { hours, minutes } = getTimePartsInTimezone(isoString, timeZoneId);
  const period = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return minutes === 0
    ? `${h12}:00 ${period}`
    : `${h12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatEventDate(isoString: string, timeZoneId?: string): string {
  if (timeZoneId) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timeZoneId,
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(isoString));
  }
  return format(new Date(isoString), 'EEEE, MMMM d, yyyy');
}

/**
 * Convert HTML description from Google Calendar to plain text.
 * Converts <br> to newlines, strips tags, and decodes HTML entities.
 */
function htmlToPlainText(html: string): string {
  let text = html;
  // Convert <br>, <br/>, <br /> to newlines
  text = text.replace(/<br\s*\/?>/gi, '\n');
  // Convert block-level tags to newlines
  text = text.replace(/<\/(p|div|li)>/gi, '\n');
  // Strip remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');
  // Decode common HTML entities
  text = text.replace(/&amp;/g, '&');
  text = text.replace(/&lt;/g, '<');
  text = text.replace(/&gt;/g, '>');
  text = text.replace(/&quot;/g, '"');
  text = text.replace(/&#39;/g, "'");
  text = text.replace(/&nbsp;/g, ' ');
  // Collapse multiple blank lines into one
  text = text.replace(/\n{3,}/g, '\n\n');
  return text.trim();
}

function formatDuration(startTime: string, endTime: string): string {
  const start = new Date(startTime);
  const end = new Date(endTime);
  const diffMs = end.getTime() - start.getTime();
  const totalMinutes = Math.round(diffMs / 60000);

  if (totalMinutes < 60) {
    return `${totalMinutes} minutes`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (mins === 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  }
  return `${hours}h ${mins}m`;
}

// ============================================================================
// Component
// ============================================================================

export function CalendarEventDetailsDialog({
  open,
  onClose,
  event,
  timeZoneId,
}: CalendarEventDetailsDialogProps) {
  if (!event) return null;

  const color = event.calendarColor || '#4f46e5';

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          {/* pr-10 keeps a long title clear of the dialog's ✕. */}
          <div className="flex items-start gap-3 pr-10">
            {/* Color dot */}
            <div
              className="mt-1 h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: color }}
            />
            <DialogTitle className="text-lg leading-snug">
              {event.title}
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Calendar source */}
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm" style={{ color }}>
              {event.calendarName}
            </span>
          </div>

          {/* Date & Time */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {event.isAllDay
                ? formatEventDate(event.startTime, timeZoneId)
                : `${formatEventDate(event.startTime, timeZoneId)}`}
            </span>
          </div>

          {!event.isAllDay && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {formatEventTime(event.startTime, timeZoneId)} &ndash;{' '}
                {formatEventTime(event.endTime, timeZoneId)}
                {timeZoneId && (
                  <span className="text-muted-foreground ml-1">
                    {getTimezoneAbbreviation(timeZoneId)}
                  </span>
                )}
                <span className="text-muted-foreground ml-2">
                  ({formatDuration(event.startTime, event.endTime)})
                </span>
              </span>
            </div>
          )}

          {/* Location */}
          {event.location && (
            <>
              <hr className="border-border" />
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
                <span className="text-sm">{event.location}</span>
              </div>
            </>
          )}

          {/* Description */}
          {event.description && (
            <>
              <hr className="border-border" />
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                  {htmlToPlainText(event.description)}
                </p>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CalendarEventDetailsDialog;
